import { useCallback, useRef, useState } from "react";
import {
  Sparkles,
  Loader2,
  TrendingUp,
  ThumbsUp,
  AlertTriangle,
  Upload,
  X,
  Film,
  Bookmark,
  Check,
  Trophy,
  Columns,
  Zap,
} from "lucide-react";

import { cn } from "../../../lib/utils";
import { Button } from "../../ui/button";
import { Input } from "../../ui/input";
import { Textarea } from "../../ui/textarea";
import { Badge } from "../../ui/badge";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../../ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../ui/select";

const SECTORS = [
  "business", "fitness", "e-commerce", "food", "fashion", "tech",
  "travel", "education", "beauty", "real-estate", "automotive", "entertainment",
];

const COUNTRIES = [
  { code: "BE", label: "België" },
  { code: "NL", label: "Nederland" },
  { code: "FR", label: "Frankrijk" },
  { code: "DE", label: "Duitsland" },
  { code: "GB", label: "Verenigd Koninkrijk" },
  { code: "US", label: "Verenigde Staten" },
];

const EMPTY = {
  hook_text: "",
  caption: "",
  sector: "",
  country: "",
  target_audience: "",
  video_description: "",
};

const Field = ({ label, children }) => (
  <label className="block">
    <span className="block text-[11px] font-semibold uppercase tracking-wider text-gray-500 mb-1">
      {label}
    </span>
    {children}
  </label>
);

const scoreColor = (score, isDark) => {
  if (score >= 75) return isDark ? "text-emerald-400" : "text-emerald-600";
  if (score >= 50) return isDark ? "text-yellow-400" : "text-yellow-600";
  return isDark ? "text-red-400" : "text-red-600";
};

const extractFrames = (file) =>
  new Promise((resolve) => {
    const objectUrl = URL.createObjectURL(file);
    const video = document.createElement("video");
    video.muted = true;
    video.preload = "metadata";
    video.crossOrigin = "anonymous";

    const canvas = document.createElement("canvas");
    const ctx = canvas.getContext("2d");

    const captureAt = (time) =>
      new Promise((res) => {
        video.currentTime = time;
        const handler = () => {
          video.removeEventListener("seeked", handler);
          const maxW = 640;
          const scale = Math.min(1, maxW / (video.videoWidth || maxW));
          canvas.width = Math.round((video.videoWidth || 640) * scale);
          canvas.height = Math.round((video.videoHeight || 360) * scale);
          ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
          const dataUrl = canvas.toDataURL("image/jpeg", 0.75);
          res({ dataUrl, base64: dataUrl.split(",")[1] });
        };
        video.addEventListener("seeked", handler, { once: true });
      });

    video.addEventListener("loadedmetadata", async () => {
      const dur = video.duration || 0;
      const timestamps =
        dur >= 3 ? [0, 1, Math.min(3, dur - 0.1)] : [0, Math.max(0, dur * 0.5)];

      const frames = [];
      for (const t of timestamps) {
        try {
          frames.push(await captureAt(Math.max(0, Math.min(t, dur - 0.05))));
        } catch (_) {
          // skip failed frame
        }
      }

      URL.revokeObjectURL(objectUrl);
      video.src = "";
      resolve(frames);
    });

    video.addEventListener("error", () => {
      URL.revokeObjectURL(objectUrl);
      resolve([]);
    });

    video.src = objectUrl;
  });

// ============================================================
// Single concept form block — reusable for A/B variants.
// ============================================================

const ConceptForm = ({
  form,
  setForm,
  isDark,
  videoFile,
  frames,
  extracting,
  frameError,
  onVideoChange,
  onClearVideo,
  fileInputRef,
  title,
}) => {
  return (
    <div className="space-y-3">
      {title && (
        <div className="flex items-center gap-2">
          <Badge variant="outline" className="text-[10px] h-[18px] px-1.5">
            {title}
          </Badge>
        </div>
      )}

      <div>
        <span className="block text-[11px] font-semibold uppercase tracking-wider text-gray-500 mb-1">
          Video uploaden (optioneel)
        </span>
        {!videoFile && !extracting && (
          <button
            type="button"
            onClick={() => fileInputRef.current?.click()}
            className={cn(
              "w-full rounded-lg border-2 border-dashed p-3 text-center transition-colors",
              isDark
                ? "border-white/[0.10] hover:border-white/20 text-gray-500 hover:text-gray-300"
                : "border-gray-200 hover:border-gray-300 text-gray-400 hover:text-gray-600"
            )}
          >
            <Upload className="h-4 w-4 mx-auto mb-1 opacity-60" />
            <p className="text-[11px]">Klik om video te selecteren</p>
          </button>
        )}
        <input
          ref={fileInputRef}
          type="file"
          accept="video/*"
          className="hidden"
          onChange={onVideoChange}
        />

        {extracting && (
          <div
            className={cn(
              "flex items-center gap-2 rounded-lg border p-2 text-[11px]",
              isDark ? "bg-white/[0.03] border-white/[0.06] text-gray-400" : "bg-gray-50 border-gray-200 text-gray-600"
            )}
          >
            <Loader2 className="h-3 w-3 animate-spin flex-shrink-0" />
            <span>Frames extraheren...</span>
          </div>
        )}

        {frames.length > 0 && videoFile && (
          <div
            className={cn(
              "rounded-lg border p-2 space-y-1.5",
              isDark ? "bg-white/[0.02] border-white/[0.06]" : "bg-gray-50/70 border-gray-200"
            )}
          >
            <div className="flex items-center justify-between gap-2">
              <div className="flex items-center gap-1.5 min-w-0">
                <Film className={cn("h-3 w-3 flex-shrink-0", isDark ? "text-gray-400" : "text-gray-500")} />
                <span className={cn("text-[10px] truncate", isDark ? "text-gray-400" : "text-gray-600")}>
                  {videoFile.name}
                </span>
              </div>
              <button
                type="button"
                onClick={onClearVideo}
                className={cn(
                  "flex-shrink-0 rounded p-0.5 transition-colors",
                  isDark ? "text-gray-500 hover:text-gray-300 hover:bg-white/[0.06]" : "text-gray-400 hover:text-gray-600 hover:bg-gray-200"
                )}
              >
                <X className="h-3 w-3" />
              </button>
            </div>
            <div className="flex gap-1.5">
              {frames.map((f, i) => (
                <img
                  key={i}
                  src={f.dataUrl}
                  alt={`Frame ${i + 1}`}
                  className="rounded h-12 object-cover flex-1 min-w-0"
                />
              ))}
            </div>
          </div>
        )}

        {frameError && <p className="text-[10px] text-red-500 mt-1">{frameError}</p>}
      </div>

      <Field label="Hook (eerste 1-3 seconden)">
        <Input
          value={form.hook_text}
          onChange={(e) => setForm((s) => ({ ...s, hook_text: e.target.value }))}
          placeholder="bv. 'Niemand vertelt je dit over...'"
        />
      </Field>

      <Field label="Caption">
        <Textarea
          value={form.caption}
          onChange={(e) => setForm((s) => ({ ...s, caption: e.target.value }))}
          rows={2}
          placeholder="Tekst onder de video"
        />
      </Field>

      <div className="grid grid-cols-2 gap-3">
        <Field label="Sector">
          <Select
            value={form.sector || "none"}
            onValueChange={(v) => setForm((s) => ({ ...s, sector: v === "none" ? "" : v }))}
          >
            <SelectTrigger><SelectValue placeholder="Kies sector" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="none">Geen</SelectItem>
              {SECTORS.map((s) => (
                <SelectItem key={s} value={s}>{s}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </Field>

        <Field label="Land">
          <Select
            value={form.country || "none"}
            onValueChange={(v) => setForm((s) => ({ ...s, country: v === "none" ? "" : v }))}
          >
            <SelectTrigger><SelectValue placeholder="Kies land" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="none">Geen</SelectItem>
              {COUNTRIES.map((c) => (
                <SelectItem key={c.code} value={c.code}>{c.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </Field>
      </div>

      <Field label="Doelgroep (optioneel)">
        <Input
          value={form.target_audience}
          onChange={(e) => setForm((s) => ({ ...s, target_audience: e.target.value }))}
          placeholder="bv. 'Jonge ondernemers, 25-35'"
        />
      </Field>

      <Field label="Video beschrijving (optioneel)">
        <Textarea
          value={form.video_description}
          onChange={(e) => setForm((s) => ({ ...s, video_description: e.target.value }))}
          rows={2}
          placeholder="Wat zie je in de video?"
        />
      </Field>
    </div>
  );
};

// ============================================================
// Result block — reusable for A/B
// ============================================================

const ResultBlock = ({ result, isDark, isWinner, label }) => {
  if (!result) return null;
  return (
    <div
      className={cn(
        "rounded-xl border p-3 space-y-2.5 relative",
        isDark ? "bg-white/[0.03] border-white/[0.06]" : "bg-gray-50/50 border-gray-200/70",
        isWinner && (isDark ? "border-emerald-500/40 ring-1 ring-emerald-500/20" : "border-emerald-400 ring-1 ring-emerald-200")
      )}
    >
      {label && (
        <div className="flex items-center justify-between">
          <Badge variant="outline" className="text-[10px] h-[18px] px-1.5">
            {label}
          </Badge>
          {isWinner && (
            <span
              className={cn(
                "flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold",
                isDark ? "bg-emerald-500/15 text-emerald-300" : "bg-emerald-50 text-emerald-700"
              )}
            >
              <Trophy className="h-3 w-3" /> Winnaar
            </span>
          )}
        </div>
      )}

      <div className="flex items-center justify-between gap-3">
        <div>
          <p className={cn("text-[10px] uppercase tracking-wider font-semibold", isDark ? "text-gray-500" : "text-gray-400")}>
            Voorspelde score
          </p>
          <p className={cn("text-[28px] font-bold leading-tight", scoreColor(result.predicted_score, isDark))}>
            {Math.round(result.predicted_score)}
            <span className={cn("text-[13px] ml-1", isDark ? "text-gray-500" : "text-gray-400")}>/ 100</span>
          </p>
        </div>
        {result.predicted_engagement_rate && (
          <div className="text-right">
            <p className={cn("text-[10px] uppercase tracking-wider font-semibold", isDark ? "text-gray-500" : "text-gray-400")}>
              Engagement
            </p>
            <p className={cn("text-[16px] font-semibold", isDark ? "text-white" : "text-gray-900")}>
              {result.predicted_engagement_rate}
            </p>
          </div>
        )}
      </div>

      {result.visual_analysis && (
        <ResultSection label="Visueel" color={isDark ? "text-blue-400" : "text-blue-600"} text={result.visual_analysis} isDark={isDark} />
      )}
      {result.audience_fit && (
        <ResultSection label="Doelgroep fit" color={isDark ? "text-purple-400" : "text-purple-600"} text={result.audience_fit} isDark={isDark} />
      )}
      {result.library_comparison && (
        <ResultSection label="Vergelijking" color={isDark ? "text-gray-400" : "text-gray-600"} text={result.library_comparison} isDark={isDark} />
      )}
      {result.hook_analysis && (
        <ResultSection label="Hook" color={isDark ? "text-gray-400" : "text-gray-600"} text={result.hook_analysis} isDark={isDark} />
      )}
      {result.caption_analysis && (
        <ResultSection label="Caption" color={isDark ? "text-gray-400" : "text-gray-600"} text={result.caption_analysis} isDark={isDark} />
      )}

      {result.strengths?.length > 0 && (
        <div>
          <p className={cn("text-[10px] font-semibold uppercase tracking-wider mb-1 flex items-center gap-1", isDark ? "text-emerald-400" : "text-emerald-600")}>
            <ThumbsUp className="h-3 w-3" /> Sterke punten
          </p>
          <ul className="space-y-0.5">
            {result.strengths.slice(0, 4).map((s, i) => (
              <li key={i} className={cn("text-[11px] flex gap-1", isDark ? "text-gray-300" : "text-gray-700")}>
                <span className="text-emerald-500">+</span>{s}
              </li>
            ))}
          </ul>
        </div>
      )}

      {result.improvements?.length > 0 && (
        <div>
          <p className={cn("text-[10px] font-semibold uppercase tracking-wider mb-1 flex items-center gap-1", isDark ? "text-yellow-400" : "text-yellow-600")}>
            <AlertTriangle className="h-3 w-3" /> Verbeterpunten
          </p>
          <ul className="space-y-0.5">
            {result.improvements.slice(0, 4).map((s, i) => (
              <li key={i} className={cn("text-[11px] flex gap-1", isDark ? "text-gray-300" : "text-gray-700")}>
                <span className="text-yellow-500">!</span>{s}
              </li>
            ))}
          </ul>
        </div>
      )}

      {result.benchmark && result.benchmark.sampleSize > 0 && (
        <div className={cn("flex items-center gap-1.5 text-[10px]", isDark ? "text-gray-500" : "text-gray-500")}>
          <TrendingUp className="h-3 w-3" />
          Vergeleken met {result.benchmark.sampleSize} video's
        </div>
      )}
    </div>
  );
};

const ResultSection = ({ label, color, text, isDark }) => (
  <div>
    <p className={cn("text-[10px] font-semibold uppercase tracking-wider mb-0.5", color)}>{label}</p>
    <p className={cn("text-[11px]", isDark ? "text-gray-300" : "text-gray-700")}>{text}</p>
  </div>
);

// ============================================================
// Main dialog
// ============================================================

const PreviewScoreDialog = ({ open, onOpenChange, previewScore, savePreviewScore, isDark }) => {
  const [abMode, setAbMode] = useState(false);

  // A variant
  const [formA, setFormA] = useState(EMPTY);
  const [framesA, setFramesA] = useState([]);
  const [videoFileA, setVideoFileA] = useState(null);
  const [extractingA, setExtractingA] = useState(false);
  const [frameErrorA, setFrameErrorA] = useState(null);
  const fileInputRefA = useRef(null);

  // B variant
  const [formB, setFormB] = useState(EMPTY);
  const [framesB, setFramesB] = useState([]);
  const [videoFileB, setVideoFileB] = useState(null);
  const [extractingB, setExtractingB] = useState(false);
  const [frameErrorB, setFrameErrorB] = useState(null);
  const fileInputRefB = useRef(null);

  // Results
  const [resultA, setResultA] = useState(null);
  const [resultB, setResultB] = useState(null);
  const [submittedFormA, setSubmittedFormA] = useState(null);
  const [submittedFormB, setSubmittedFormB] = useState(null);

  const [submitting, setSubmitting] = useState(false);
  const [err, setErr] = useState(null);
  const [saving, setSaving] = useState(null); // 'A' | 'B' | null
  const [saved, setSaved] = useState({ A: false, B: false });

  const makeVideoHandler = (setVideoFile, setFrames, setFrameError, setExtracting) =>
    async (e) => {
      const file = e.target.files?.[0];
      if (!e.target.files) return;
      e.target.value = "";
      if (!file) return;
      setVideoFile(file);
      setFrames([]);
      setFrameError(null);
      setExtracting(true);
      try {
        const extracted = await extractFrames(file);
        if (extracted.length === 0) {
          setFrameError("Kon geen frames extraheren.");
          setVideoFile(null);
        } else {
          setFrames(extracted);
        }
      } catch (_) {
        setFrameError("Video verwerking mislukt.");
        setVideoFile(null);
      } finally {
        setExtracting(false);
      }
    };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const handleVideoA = useCallback(makeVideoHandler(setVideoFileA, setFramesA, setFrameErrorA, setExtractingA), []);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const handleVideoB = useCallback(makeVideoHandler(setVideoFileB, setFramesB, setFrameErrorB, setExtractingB), []);

  const clearA = useCallback(() => {
    setVideoFileA(null); setFramesA([]); setFrameErrorA(null); setExtractingA(false);
  }, []);
  const clearB = useCallback(() => {
    setVideoFileB(null); setFramesB([]); setFrameErrorB(null); setExtractingB(false);
  }, []);

  const buildPayload = (form, frames) => {
    const payload = {
      hook_text: form.hook_text || null,
      caption: form.caption || null,
      sector: form.sector || null,
      country: form.country || null,
      target_audience: form.target_audience || null,
      video_description: form.video_description || null,
    };
    if (frames.length > 0) payload.frames = frames.map((f) => f.base64);
    return payload;
  };

  const handleSubmit = async () => {
    setErr(null);
    setSubmitting(true);
    setSaved({ A: false, B: false });
    try {
      if (abMode) {
        const [a, b] = await Promise.all([
          previewScore(buildPayload(formA, framesA)),
          previewScore(buildPayload(formB, framesB)),
        ]);
        setResultA(a);
        setResultB(b);
        setSubmittedFormA({ ...formA });
        setSubmittedFormB({ ...formB });
      } else {
        const a = await previewScore(buildPayload(formA, framesA));
        setResultA(a);
        setSubmittedFormA({ ...formA });
        setResultB(null);
        setSubmittedFormB(null);
      }
    } catch (e) {
      const code = e?.response?.data?.code;
      if (code === "NO_CREDITS") {
        setErr("no_credits");
      } else {
        setErr(e?.response?.data?.message || e?.message || "Preview score genereren mislukt");
      }
    } finally {
      setSubmitting(false);
    }
  };

  const handleSave = async (variant) => {
    const result = variant === "A" ? resultA : resultB;
    const sub = variant === "A" ? submittedFormA : submittedFormB;
    if (!result || !savePreviewScore) return;
    setSaving(variant);
    try {
      await savePreviewScore({
        ...result,
        sector: sub?.sector || null,
        country: sub?.country || null,
        input: {
          hook_text: sub?.hook_text || null,
          caption: sub?.caption || null,
          target_audience: sub?.target_audience || null,
          video_description: sub?.video_description || null,
        },
      });
      setSaved((s) => ({ ...s, [variant]: true }));
    } catch (e) {
      setErr(e?.response?.data?.message || e?.message || "Opslaan mislukt");
    } finally {
      setSaving(null);
    }
  };

  const handleClose = (next) => {
    if (!next) {
      setResultA(null); setResultB(null);
      setSubmittedFormA(null); setSubmittedFormB(null);
      setErr(null);
      setFormA(EMPTY); setFormB(EMPTY);
      clearA(); clearB();
      setSaved({ A: false, B: false });
    }
    onOpenChange(next);
  };

  // Compute winner in A/B mode
  const winner =
    abMode && resultA && resultB
      ? resultA.predicted_score >= resultB.predicted_score ? "A" : "B"
      : null;

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className={cn("max-h-[90vh] overflow-y-auto", abMode ? "max-w-5xl" : "max-w-2xl")}>
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 justify-between">
            <span className="flex items-center gap-2">
              <Sparkles className="h-4 w-4" />
              Preview score
              {abMode && (
                <Badge variant="outline" className="text-[10px] h-[18px] px-1.5">A/B</Badge>
              )}
            </span>
            <button
              type="button"
              onClick={() => setAbMode((m) => !m)}
              className={cn(
                "flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-medium border transition-colors",
                abMode
                  ? isDark ? "bg-indigo-500/20 text-indigo-300 border-indigo-500/30" : "bg-indigo-50 text-indigo-700 border-indigo-200"
                  : isDark ? "bg-transparent text-gray-400 border-white/[0.08]" : "bg-transparent text-gray-600 border-gray-200"
              )}
            >
              <Columns className="h-3 w-3" />
              A/B modus
            </button>
          </DialogTitle>
          <DialogDescription>
            {abMode
              ? "Vul twee concepten in. Claude scoort beide tegelijk en duidt de winnaar aan."
              : "Upload je video of beschrijf je concept. Claude voorspelt de score op basis van je bibliotheek."}
          </DialogDescription>
        </DialogHeader>

        {abMode ? (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <ConceptForm
              form={formA}
              setForm={setFormA}
              isDark={isDark}
              videoFile={videoFileA}
              frames={framesA}
              extracting={extractingA}
              frameError={frameErrorA}
              onVideoChange={handleVideoA}
              onClearVideo={clearA}
              fileInputRef={fileInputRefA}
              title="Variant A"
            />
            <ConceptForm
              form={formB}
              setForm={setFormB}
              isDark={isDark}
              videoFile={videoFileB}
              frames={framesB}
              extracting={extractingB}
              frameError={frameErrorB}
              onVideoChange={handleVideoB}
              onClearVideo={clearB}
              fileInputRef={fileInputRefB}
              title="Variant B"
            />
          </div>
        ) : (
          <ConceptForm
            form={formA}
            setForm={setFormA}
            isDark={isDark}
            videoFile={videoFileA}
            frames={framesA}
            extracting={extractingA}
            frameError={frameErrorA}
            onVideoChange={handleVideoA}
            onClearVideo={clearA}
            fileInputRef={fileInputRefA}
          />
        )}

        {err === "no_credits" ? (
          <div className={cn("rounded-md border p-3 text-[12px] flex items-center justify-between gap-3",
            isDark ? "bg-yellow-500/[0.08] border-yellow-500/20 text-yellow-300" : "bg-yellow-50 border-yellow-200 text-yellow-800"
          )}>
            <span><Zap className="h-3.5 w-3.5 inline mr-1" />Geen AI-tokens meer. Koop extra tokens om door te gaan.</span>
            <button onClick={() => onOpenChange(false)} className="text-[11px] font-semibold underline whitespace-nowrap">Koop tokens</button>
          </div>
        ) : err ? (
          <div className={cn("rounded-md border p-2 text-[12px]",
            isDark ? "bg-red-500/[0.08] border-red-500/20 text-red-300" : "bg-red-50 border-red-200 text-red-700"
          )}>
            {err}
          </div>
        ) : null}

        {(resultA || resultB) && (
          <div className={cn("grid gap-3", abMode ? "grid-cols-1 md:grid-cols-2" : "grid-cols-1")}>
            {resultA && (
              <div className="space-y-2">
                <ResultBlock
                  result={resultA}
                  isDark={isDark}
                  isWinner={winner === "A"}
                  label={abMode ? "Variant A" : null}
                />
                <div className="flex justify-end">
                  {saved.A ? (
                    <span className={cn("flex items-center gap-1.5 text-[11px] font-medium", isDark ? "text-emerald-400" : "text-emerald-600")}>
                      <Check className="h-3 w-3" /> Opgeslagen
                    </span>
                  ) : (
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => handleSave("A")}
                      disabled={saving === "A"}
                      className="gap-1.5 h-7 text-[11px]"
                    >
                      {saving === "A" ? <Loader2 className="h-3 w-3 animate-spin" /> : <Bookmark className="h-3 w-3" />}
                      Bewaar
                    </Button>
                  )}
                </div>
              </div>
            )}
            {resultB && (
              <div className="space-y-2">
                <ResultBlock
                  result={resultB}
                  isDark={isDark}
                  isWinner={winner === "B"}
                  label="Variant B"
                />
                <div className="flex justify-end">
                  {saved.B ? (
                    <span className={cn("flex items-center gap-1.5 text-[11px] font-medium", isDark ? "text-emerald-400" : "text-emerald-600")}>
                      <Check className="h-3 w-3" /> Opgeslagen
                    </span>
                  ) : (
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => handleSave("B")}
                      disabled={saving === "B"}
                      className="gap-1.5 h-7 text-[11px]"
                    >
                      {saving === "B" ? <Loader2 className="h-3 w-3 animate-spin" /> : <Bookmark className="h-3 w-3" />}
                      Bewaar
                    </Button>
                  )}
                </div>
              </div>
            )}
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={() => handleClose(false)}>
            Sluiten
          </Button>
          <Button onClick={handleSubmit} disabled={submitting || extractingA || extractingB}>
            {submitting && <Loader2 className="h-3.5 w-3.5 animate-spin mr-1.5" />}
            {resultA || resultB ? "Opnieuw scoren" : abMode ? "Score beide" : "Score voorspellen"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default PreviewScoreDialog;
